import React, { useState, useRef, useEffect } from 'react';
import '../styles/DatePicker.css';

const DatePicker = ({ value, onChange, error, placeholder = 'Select Date of Birth' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);

  // Initialize to 25 years ago for better UX (typical job applicant age)
  const getDefaultMonth = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 25);
    return date;
  };

  const [currentMonth, setCurrentMonth] = useState(getDefaultMonth());
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatISODate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate);
    onChange(formatISODate(newDate));
    setIsOpen(false);
  };

  const handleMonthChange = (increment) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + increment, 1);
    // Don't allow navigation to months that would be less than 18 years ago
    if (!isMonthDisabled(newDate)) {
      setCurrentMonth(newDate);
    }
  };

  const handleYearChange = (increment) => {
    const newDate = new Date(currentMonth.getFullYear() + increment, currentMonth.getMonth(), 1);
    // Don't allow navigation to years that would be less than 18 years ago
    if (!isMonthDisabled(newDate)) {
      setCurrentMonth(newDate);
    }
  };

  const isMonthDisabled = (date) => {
    // Check if navigating to this month would be invalid (less than 18 years ago)
    const today = new Date();
    const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

    // Get the last day of the month to check
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    // If the last day of this month is less than 18 years ago, disable navigation
    return lastDayOfMonth > eighteenYearsAgo;
  };

  const isSelected = (day) => {
    if (!selectedDate || !day) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isFutureDate = (day) => {
    if (!day) return false;
    const today = new Date();
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

    // Calculate age based on selected date
    let age = today.getFullYear() - checkDate.getFullYear();
    const monthDiff = today.getMonth() - checkDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < checkDate.getDate())) {
      age--;
    }

    // Disable if date is in the future or if age would be less than 18
    return checkDate > today || age < 18;
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth(currentMonth);
  const currentYear = currentMonth.getFullYear();
  const currentMonthName = months[currentMonth.getMonth()];

  return (
    <div className="date-picker-container" ref={dropdownRef}>
      <div
        className={`date-picker-input ${error ? 'error' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="date-picker-value">
          {selectedDate ? (
            <span className="selected-date">{formatDisplayDate(selectedDate)}</span>
          ) : (
            <span className="placeholder">{placeholder}</span>
          )}
        </div>
        <svg className="calendar-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M3 8H17" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M7 2V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M13 2V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>

      {isOpen && (
        <div className="date-picker-dropdown">
          <div className="date-picker-header">
            <button
              type="button"
              className="nav-button"
              onClick={() => handleYearChange(-1)}
              aria-label="Previous year"
            >
              &laquo;
            </button>
            <button
              type="button"
              className="nav-button"
              onClick={() => handleMonthChange(-1)}
              aria-label="Previous month"
            >
              &lsaquo;
            </button>
            <div className="current-month">
              {currentMonthName} {currentYear}
            </div>
            <button
              type="button"
              className="nav-button"
              onClick={() => handleMonthChange(1)}
              disabled={isMonthDisabled(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              &rsaquo;
            </button>
            <button
              type="button"
              className="nav-button"
              onClick={() => handleYearChange(1)}
              disabled={isMonthDisabled(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1))}
              aria-label="Next year"
            >
              &raquo;
            </button>
          </div>

          <div className="date-picker-calendar">
            <div className="weekdays">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <div key={day} className="weekday">{day}</div>
              ))}
            </div>
            <div className="days-grid">
              {days.map((day, index) => (
                <button
                  key={index}
                  type="button"
                  className={`day ${!day ? 'empty' : ''} ${isSelected(day) ? 'selected' : ''} ${isToday(day) ? 'today' : ''} ${isFutureDate(day) ? 'future' : ''}`}
                  onClick={() => day && !isFutureDate(day) && handleDateSelect(day)}
                  disabled={!day || isFutureDate(day)}
                >
                  {day || ''}
                </button>
              ))}
            </div>
          </div>

          <div className="date-picker-footer">
            <button
              type="button"
              className="quick-select-btn"
              onClick={() => {
                // Set to 25 years ago (common age for job applications)
                const date = new Date();
                date.setFullYear(date.getFullYear() - 25);
                setCurrentMonth(date);
              }}
            >
              25 years ago
            </button>
            <button
              type="button"
              className="quick-select-btn"
              onClick={() => {
                // Set to 30 years ago
                const date = new Date();
                date.setFullYear(date.getFullYear() - 30);
                setCurrentMonth(date);
              }}
            >
              30 years ago
            </button>
            <button
              type="button"
              className="clear-btn"
              onClick={() => {
                setSelectedDate(null);
                onChange('');
                setIsOpen(false);
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {error && <div className="field-error">{error}</div>}
    </div>
  );
};

export default DatePicker;
